import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import TablePagination from "./TablePagination";
import { getPastExpoDetails, downloadPastExpoExcel } from "../Apis/ExpoAPIs";
import { getVisitorEnquiriesByExpo, downloadVisitorEnquiriesExcel, sendBulkVisitorSMS } from "../Apis/VisitorEnquiryAPIs";
import { getKnownSources } from "../Apis/KnownSourceAPIs";
import { getImageUrl, BASE_URL, api } from "../Apis/api";
import { formatDate, formatDateTime } from "../helper/DateHelper";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";

const PastExpoViewLayer = () => {
  const { id } = useParams();
  const [expo, setExpo] = useState(null);
  const [stallBookings, setStallBookings] = useState([]);
  const [visitorBookings, setVisitorBookings] = useState([]);
  const [mainTab, setMainTab] = useState("stalls");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [selectedStallProducts, setSelectedStallProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);

  const [totalRevenue, setTotalRevenue] = useState(0);

  // Visitor filter & search states
  const [visitorEnquiries, setVisitorEnquiries] = useState([]);
  const [visitorTotal, setVisitorTotal] = useState(0);
  const [detailSearchTerm, setDetailSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [knownSourceFilter, setKnownSourceFilter] = useState("All");
  const [visitorKnownSourceCounts, setVisitorKnownSourceCounts] = useState({ All: 0 });
  const [dbKnownSources, setDbKnownSources] = useState([]);
  const [sourceCounts, setSourceCounts] = useState({ all: 0, website: 0, adminApp: 0, userApp: 0 });
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingStallExcel, setExportingStallExcel] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Property Expo Bulk SMS & WhatsApp Selection
  const [selectedVisitorIds, setSelectedVisitorIds] = useState([]);
  const [sendingSMS, setSendingSMS] = useState(false);

  const isPropertyExpo = React.useMemo(() => {
    if (!expo) return false;
    const name = (expo.expoName || expo.name || "").toLowerCase();
    const brand = (expo.expoBrandId?.name || expo.expoBrandName || "").toLowerCase();
    const cat = (expo.category?.name || expo.categoryName || "").toLowerCase();
    return name.includes("property") || brand.includes("property") || cat.includes("property");
  }, [expo]);

  const handleSendBulkSMS = async () => {
    if (selectedVisitorIds.length === 0) {
      toast.warning("Please select at least one visitor to send SMS");
      return;
    }

    const selectedVisitors = visitorEnquiries.filter((v) =>
      selectedVisitorIds.includes(v._id || v.id)
    );
    const numbers = selectedVisitors
      .map((v) => v.mobileNo || v.phone || v.mobile1)
      .filter(Boolean);

    if (numbers.length === 0) {
      toast.warning("No valid mobile numbers found for selected visitors");
      return;
    }

    try {
      setSendingSMS(true);
      const res = await sendBulkVisitorSMS({ numbers });
      if (res?.status) {
        toast.success(res.response?.message || `SMS sent successfully to ${numbers.length} visitor(s)!`);
        setSelectedVisitorIds([]);
      } else {
        toast.error(res?.response?.message || "Failed to send SMS");
      }
    } catch (err) {
      console.error("Error sending SMS:", err);
      toast.error("An error occurred while sending SMS");
    } finally {
      setSendingSMS(false);
    }
  };

  useEffect(() => {
    const loadKnownSources = async () => {
      try {
        const res = await getKnownSources();
        if (res?.status) {
          const list = res.response?.data || res.response || [];
          setDbKnownSources(list.filter((item) => item.isActive !== false));
        }
      } catch (err) {
        console.error("Failed to load known sources:", err);
      }
    };
    loadKnownSources();
  }, []);

  const knownSourceList = React.useMemo(() => {
    const list = ["All"];
    dbKnownSources.forEach((ks) => {
      if (ks.name && !list.includes(ks.name)) {
        list.push(ks.name);
      }
    });
    Object.keys(visitorKnownSourceCounts).forEach((key) => {
      if (!list.includes(key) && key !== "All") {
        list.push(key);
      }
    });
    return list;
  }, [dbKnownSources, visitorKnownSourceCounts]);

  const fetchDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getPastExpoDetails(id);
      if (res.status) {
        const data = res.response?.data || res.response;
        setExpo(data.expo || data);
        setStallBookings(data.stallBooked?.data || data.bookedStalls || []);
        const vList = data.visitorBooked?.data || data.bookedVisitors || [];
        setVisitorBookings(vList);
        setVisitorEnquiries(vList);
        setVisitorTotal(data.visitorBooked?.total || vList.length);
        setTotalRevenue(
          data.stallBooked?.totalRevenue || data.totalRevenue || 0,
        );
      }
    } catch (error) {
      console.error("Error fetching past expo details:", error);
    }
    setLoading(false);
  };

  const fetchVisitorEnquiries = async (
    expoId,
    searchInput = detailSearchTerm,
    pageNum = currentPage,
    limitVal = rowsPerPage,
    currentSourceFilter = sourceFilter,
    currentKnownSourceFilter = knownSourceFilter
  ) => {
    try {
      let apiSource = "";
      if (currentSourceFilter === "Website") apiSource = "website";
      else if (currentSourceFilter === "Admin-app") apiSource = "mobile-admin";
      else if (currentSourceFilter === "User-app") apiSource = "user-app";

      const apiKnownSource = currentKnownSourceFilter === "All" ? "" : currentKnownSourceFilter;

      const res = await getVisitorEnquiriesByExpo(
        expoId,
        searchInput,
        pageNum,
        limitVal,
        apiSource,
        apiKnownSource
      );
      if (res.status) {
        const raw = res.response?.data || res.response || [];
        const data = Array.isArray(raw) ? raw : [];
        setVisitorEnquiries(data);
        setVisitorTotal(res.response?.total || data.length);
      }
    } catch (err) {
      console.error("Error fetching visitor enquiries:", err);
    }
  };

  const fetchVisitorCounts = async (expoId) => {
    try {
      const res = await getVisitorEnquiriesByExpo(expoId, "", undefined, 10000);
      if (res.status) {
        const raw = res.response?.data || res.response || [];
        const data = Array.isArray(raw) ? raw : [];
        let web = 0, adm = 0, usr = 0;
        const ksCounts = { All: data.length };

        dbKnownSources.forEach((ks) => {
          if (ks.name) ksCounts[ks.name] = 0;
        });

        data.forEach((item) => {
          const s = (item.source || "").toLowerCase().trim();
          if (s === "website") web++;
          else if (s === "mobile-admin" || s === "admin-app") adm++;
          else if (s === "user-app") usr++;

          const ksVal = (
            item.sourceId?.name ||
            item.knowAboutExhibition ||
            item.statusDetails?.[0]?.knowAboutExhibition ||
            ""
          ).trim();

          if (ksVal) {
            const matched = dbKnownSources.find(
              (d) =>
                (d.name && d.name.toLowerCase() === ksVal.toLowerCase()) ||
                d._id === ksVal ||
                d.sourceId === ksVal
            );
            const targetKey = matched ? matched.name : ksVal;
            ksCounts[targetKey] = (ksCounts[targetKey] || 0) + 1;
          }
        });
        setSourceCounts({
          all: data.length,
          website: web,
          adminApp: adm,
          userApp: usr,
        });
        setVisitorKnownSourceCounts(ksCounts);
      }
    } catch (err) {
      console.error("Error fetching visitor counts:", err);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  useEffect(() => {
    if (id && mainTab === "visitors") {
      fetchVisitorCounts(id);
    }
  }, [id, mainTab, dbKnownSources]);

  useEffect(() => {
    if (id && mainTab === "visitors") {
      fetchVisitorEnquiries(id, detailSearchTerm, currentPage, rowsPerPage, sourceFilter, knownSourceFilter);
    }
  }, [id, mainTab, detailSearchTerm, currentPage, rowsPerPage, sourceFilter, knownSourceFilter]);

  if (!expo) return <div className="p-24 text-center">Loading...</div>;

  const handleDownloadPdf = async () => {
    try {
      const url = `expo/past/report/${id}`;
      const response = await api.get(url, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `Expo_Report_${id}.pdf`);
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download report. Please ensure you are logged in.");
    }
  };

  const handleExportCombinedExcel = async () => {
    if (!id) return;
    setExportingExcel(true);
    try {
      const res = await downloadPastExpoExcel(id, "all");
      if (res.status) {
        const url = window.URL.createObjectURL(new Blob([res.response]));
        const link = document.createElement("a");
        link.href = url;
        const sanitizedExpoName = (expo?.expoName || expo?.name || "Past_Expo").replace(/[^a-zA-Z0-9_-]/g, "_");
        link.setAttribute("download", `${sanitizedExpoName}_Complete_Report.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Complete Excel report downloaded successfully");
      } else {
        toast.error("Failed to download excel");
      }
    } catch (err) {
      console.error("Error downloading excel:", err);
      toast.error("Failed to download excel");
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportStallExcel = async () => {
    if (!id) return;
    setExportingStallExcel(true);
    try {
      const res = await downloadPastExpoExcel(id, "stall");
      if (res.status) {
        const url = window.URL.createObjectURL(new Blob([res.response]));
        const link = document.createElement("a");
        link.href = url;
        const sanitizedExpoName = (expo?.expoName || expo?.name || "Past_Expo").replace(/[^a-zA-Z0-9_-]/g, "_");
        link.setAttribute("download", `${sanitizedExpoName}_Stall_Bookings.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Stall Bookings Excel downloaded successfully");
      } else {
        toast.error("Failed to download excel");
      }
    } catch (err) {
      console.error("Error downloading excel:", err);
      toast.error("Failed to download excel");
    } finally {
      setExportingStallExcel(false);
    }
  };

  const handleExportVisitorExcel = async () => {
    if (!id) return;
    setExportingExcel(true);
    try {
      const apiKnownSource = knownSourceFilter === "All" ? "" : knownSourceFilter;
      const res = await downloadVisitorEnquiriesExcel(
        id,
        detailSearchTerm || "",
        sourceFilter,
        apiKnownSource
      );
      if (res.status) {
        const url = window.URL.createObjectURL(new Blob([res.response]));
        const link = document.createElement("a");
        link.href = url;
        const sanitizedExpoName = (expo?.expoName || expo?.name || "Past_Expo").replace(/[^a-zA-Z0-9_-]/g, "_");
        link.setAttribute("download", `${sanitizedExpoName}_Visitor_Enquiries.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Excel downloaded successfully");
      } else {
        toast.error("Failed to download excel");
      }
    } catch (err) {
      console.error("Error downloading excel:", err);
      toast.error("Failed to download excel");
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 border shadow-sm overflow-hidden bg-white">
      {/* Elegant Header */}
      <div
        className="py-24 px-32 border-bottom bg-neutral-10"
        style={{
          background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)",
          minHeight: "100px",
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div>
              <div className="d-flex align-items-center gap-3">
                <h4 className="mb-0 fw-bold text-white">
                  {expo.expoName || expo.name}
                </h4>
                {expo.entryType && (
                  <span className="badge bg-white text-danger-main radius-pill px-12 py-6 text-xs fw-bold border border-white">
                    {expo.entryType} Entry
                  </span>
                )}
              </div>
              <div className="d-flex flex-column gap-1 mt-8">
                {(expo.venue || expo.location?.address) && (
                  <div className="d-flex align-items-start gap-2 text-white">
                    <Icon icon="solar:map-point-bold" style={{ color: "#ff3b30", fontSize: "14px", marginTop: "4px" }} />
                    <span className="fw-semibold text-white text-xs" style={{ lineHeight: '1.4', whiteSpace: 'normal' }}>
                      {expo.venue || expo.location?.address}
                    </span>
                  </div>
                )}
                <div className="d-flex align-items-center gap-2 text-white opacity-80">
                  <Icon icon="solar:calendar-date-bold" style={{ fontSize: "14px" }} />
                  <span className="text-xs fw-medium text-white">
                    {formatDate(expo.startDate)} - {formatDate(expo.endDate)}
                  </span>
                </div>
                {(expo.startTime || expo.endTime) && (
                  <div className="d-flex align-items-center gap-2 text-white opacity-80">
                    <Icon icon="solar:clock-circle-bold" style={{ fontSize: "14px" }} />
                    <span className="text-xs fw-medium text-white">
                      {expo.startTime || "10:00 AM"} - {expo.endTime || "07:00 PM"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <button
              className="btn btn-success-300 radius-8 px-16 py-8 fw-bold d-flex align-items-center gap-2 shadow-sm text-dark"
              onClick={handleDownloadPdf}
            >
              <Icon icon="solar:file-download-bold" />
              Download Report
            </button>
            <button
              type="button"
              className="btn btn-success radius-8 px-16 py-8 fw-bold d-flex align-items-center gap-2 shadow-sm text-white"
              onClick={handleExportCombinedExcel}
              disabled={exportingExcel}
              style={{ backgroundColor: "#21a366", borderColor: "#21a366" }}
            >
              <Icon icon="ri:file-excel-2-line" className="text-xl" />
              <span>{exportingExcel ? "Exporting..." : "Download Excel"}</span>
            </button>
            <Link
              to="/past-expo"
              className="btn btn-sm radius-8 px-16 py-8 fw-bold d-flex align-items-center gap-2 text-dark shadow-sm transition-all hover-scale-105"
              style={{ fontSize: "12px", backgroundColor: "#f7e7da" }}
            >
              <Icon icon="solar:arrow-left-bold" />
              Back to List
            </Link>
          </div>
        </div>
      </div>

      {/* Simplified Summary Bar */}
      <div className="px-32 py-20 border-bottom bg-white">
        <div className="row g-4">
          <div className="col-md-3 border-end">
            <div className="d-flex align-items-center gap-3">
              <div className="w-40-px h-40-px rounded-circle bg-danger-focus text-danger-main d-flex align-items-center justify-content-center">
                <Icon icon="solar:user-bold" className="text-xl" />
              </div>
              <div>
                <div className="text-xxs fw-bold text-secondary-light uppercase tracking-wider">
                  Manager
                </div>
                <div className="text-sm fw-bold text-dark">
                  {Array.isArray(expo.eventManager)
                    ? expo.eventManager.map((m) => m.name).filter(Boolean).join(", ")
                    : (expo.eventManager?.name || expo.manager || "N/A")}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 border-end">
            <div className="d-flex align-items-center gap-3 ps-md-3">
              <div className="w-40-px h-40-px rounded-circle bg-info-focus text-info-main d-flex align-items-center justify-content-center">
                <Icon icon="solar:shop-bold" className="text-xl" />
              </div>
              <div>
                <div className="text-xxs fw-bold text-secondary-light uppercase tracking-wider">
                  Stalls
                </div>
                <div className="text-sm fw-bold text-dark">
                  {stallBookings.length} Booked
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 border-end">
            <div className="d-flex align-items-center gap-3 ps-md-3">
              <div className="w-40-px h-40-px rounded-circle bg-success-focus text-success-main d-flex align-items-center justify-content-center">
                <Icon
                  icon="solar:users-group-rounded-bold"
                  className="text-xl"
                />
              </div>
              <div>
                <div className="text-xxs fw-bold text-secondary-light uppercase tracking-wider">
                  Visitors
                </div>
                <div className="text-sm fw-bold text-dark">
                  {visitorTotal || visitorBookings.length} Registered
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="d-flex align-items-center gap-3 ps-md-3">
              <div className="w-40-px h-40-px rounded-circle bg-warning-focus text-warning-main d-flex align-items-center justify-content-center">
                <Icon icon="solar:wallet-money-bold" className="text-xl" />
              </div>
              <div>
                <div className="text-xxs fw-bold text-secondary-light uppercase tracking-wider">
                  Revenue
                </div>
                <div className="text-sm fw-bold text-dark">
                  ₹{totalRevenue.toLocaleString("en-IN")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-32 bg-neutral-50">
        {/* Dynamic Tables Section */}
        <div className="bg-white radius-16 border shadow-sm overflow-hidden">
          <ul className="nav nav-tabs nav-justified border-bottom bg-neutral-10 px-16">
            <li className="nav-item">
              <button
                className={`nav-link fw-bold py-16 text-sm ${mainTab === "stalls" ? "active text-danger border-bottom border-3 border-danger" : "text-secondary-light"}`}
                onClick={() => {
                  setMainTab("stalls");
                  setCurrentPage(0);
                }}
              >
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <Icon icon="solar:shop-bold" />
                  Stall Booked List ({stallBookings.length})
                </div>
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link fw-bold py-16 text-sm ${mainTab === "visitors" ? "active text-danger border-bottom border-3 border-danger" : "text-secondary-light"}`}
                onClick={() => {
                  setMainTab("visitors");
                  setCurrentPage(0);
                }}
              >
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <Icon icon="solar:users-group-rounded-bold" />
                  Visitor Booked List ({visitorTotal || visitorBookings.length})
                </div>
              </button>
            </li>
          </ul>

          {/* Stall Action Section */}
          {mainTab === "stalls" && (
            <div className="p-24 pb-0">
              <div className="d-flex align-items-center justify-content-end mb-24">
                <button
                  type="button"
                  className="btn btn-success d-inline-flex align-items-center gap-2 radius-8 px-16 h-40-px fw-bold text-white shadow-sm"
                  onClick={handleExportStallExcel}
                  disabled={exportingStallExcel}
                  style={{ backgroundColor: "#21a366", borderColor: "#21a366" }}
                >
                  <Icon icon="ri:file-excel-2-line" className="text-xl" />
                  <span>{exportingStallExcel ? "Exporting..." : "Export Excel"}</span>
                </button>
              </div>
            </div>
          )}

          {/* Visitor Filter Section */}
          {mainTab === "visitors" && (
            <div className="p-24 pb-0">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-24">
                <div className="navbar-search position-relative">
                  <input
                    type="text"
                    className="bg-neutral-50 h-40-px radius-8 border-0"
                    placeholder="Search leads..."
                    value={detailSearchTerm}
                    onChange={(e) => {
                      setDetailSearchTerm(e.target.value);
                      setCurrentPage(0);
                    }}
                    style={{ paddingLeft: "40px", width: "250px" }}
                  />
                  <Icon
                    icon="ion:search-outline"
                    className="position-absolute text-secondary-light text-xl"
                    style={{
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>

                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="text-sm fw-bold text-secondary-light uppercase me-12">
                      Source Filters:
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      {[
                        { id: "All", label: `All: ${sourceCounts.all}`, colorClass: "bg-dark" },
                        { id: "Website", label: `Website: ${sourceCounts.website}`, colorClass: "bg-primary-600" },
                        { id: "User-app", label: `User-app: ${sourceCounts.userApp}`, colorClass: "bg-info-600" }
                      ].map((filt) => (
                        <button
                          key={filt.id}
                          className={`d-flex align-items-center gap-2 px-12 py-6 border radius-8 transition-all ${sourceFilter === filt.id ? "bg-primary-50 border-primary-600 shadow-sm" : "bg-neutral-50"}`}
                          onClick={() => {
                            setSourceFilter(filt.id);
                            setCurrentPage(0);
                          }}
                        >
                          <div className={`w-8-px h-8-px rounded-circle ${filt.colorClass}`}></div>
                          <span className={`text-sm fw-bold uppercase ${sourceFilter === filt.id ? "text-primary-600" : "text-dark"}`}>
                            {filt.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    {isPropertyExpo && (
                      <>
                        <button
                          type="button"
                          className="btn d-inline-flex align-items-center gap-2 radius-8 px-16 h-40-px fw-bold text-white shadow-sm"
                          style={{ backgroundColor: "#25d366", borderColor: "#25d366" }}
                          onClick={() => {
                            if (selectedVisitorIds.length === 0) {
                              toast.warning("Please select at least one visitor");
                              return;
                            }
                            toast.info("WhatsApp API integration is in progress");
                          }}
                          title="Send WhatsApp Message"
                        >
                          <Icon icon="ic:baseline-whatsapp" className="text-xl" />
                          <span>WhatsApp {selectedVisitorIds.length > 0 ? `(${selectedVisitorIds.length})` : ""}</span>
                        </button>

                        <button
                          type="button"
                          className="btn d-inline-flex align-items-center gap-2 radius-8 px-16 h-40-px fw-bold text-white shadow-sm"
                          style={{ backgroundColor: "#0284c7", borderColor: "#0284c7" }}
                          onClick={handleSendBulkSMS}
                          disabled={sendingSMS}
                          title="Send Bulk SMS"
                        >
                          <Icon icon="solar:chat-round-line-bold" className="text-xl" />
                          <span>{sendingSMS ? "Sending SMS..." : `Send SMS ${selectedVisitorIds.length > 0 ? `(${selectedVisitorIds.length})` : ""}`}</span>
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      className="btn btn-success d-inline-flex align-items-center gap-2 radius-8 px-16 h-40-px fw-bold text-white shadow-sm"
                      onClick={handleExportVisitorExcel}
                      disabled={exportingExcel}
                      style={{ backgroundColor: "#21a366", borderColor: "#21a366" }}
                    >
                      <Icon icon="ri:file-excel-2-line" className="text-xl" />
                      <span>{exportingExcel ? "Exporting..." : "Export Excel"}</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap mb-24 p-16 radius-8 bg-neutral-50 border">
                <div className="text-sm fw-bold text-secondary-light uppercase me-12 d-flex align-items-center gap-2">
                  <Icon icon="solar:filter-bold-duotone" className="text-danger-600 text-lg" />
                  Known Source:
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {knownSourceList.map((ksName) => {
                    const count = visitorKnownSourceCounts[ksName] || 0;
                    const isSelected = knownSourceFilter === ksName;
                    return (
                      <button
                        key={ksName}
                        type="button"
                        className={`d-flex align-items-center gap-2 px-12 py-6 border radius-8 transition-all ${
                          isSelected
                            ? "bg-danger-50 border-danger-600 shadow-sm text-danger-600 fw-bold"
                            : "bg-white text-dark fw-medium hover-bg-neutral-100"
                        }`}
                        onClick={() => {
                          setKnownSourceFilter(ksName);
                          setCurrentPage(0);
                        }}
                      >
                        <div
                          className={`w-8-px h-8-px rounded-circle ${
                            isSelected ? "bg-danger-600" : "bg-secondary-400"
                          }`}
                        ></div>
                        <span className="text-xs">
                          {ksName}: <span className="fw-bold">{count}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ minWidth: mainTab === "visitors" ? "1100px" : "100%" }}>
              <thead
                className="text-nowrap"
                style={{
                  background: "linear-gradient(135deg, #bb0007, #ed1c24)",
                }}
              >
                {mainTab === "stalls" ? (
                  <tr>
                    <th className="text-white fw-bold py-16 px-24">Client Name</th>
                    <th className="text-white fw-bold py-16 px-24">Company Name</th>
                    <th className="text-white fw-bold py-16 px-24 text-center">Source</th>
                    <th className="text-white fw-bold py-16 px-24">Category</th>
                    <th className="text-white fw-bold py-16 px-24">Products</th>
                    <th className="text-white fw-bold py-16 px-24">Stall No</th>
                    <th className="text-white fw-bold py-16 px-24">Stall Size</th>
                    <th className="text-white fw-bold py-16 px-24">Stall Price</th>
                    <th className="text-white fw-bold py-16 px-24">Total Amount</th>
                    <th className="text-white fw-bold py-16 px-24">Discount</th>
                    <th className="text-white fw-bold py-16 px-24">Net Amount</th>
                    <th className="text-white fw-bold py-16 px-24 text-center">Approval Status</th>
                    <th className="text-white fw-bold py-16 px-24">Created By</th>
                    <th className="text-white fw-bold py-16 px-24">Booked Date</th>
                  </tr>
                ) : (
                  <tr>
                    {isPropertyExpo && (
                      <th className="text-white fw-bold py-16 px-16 text-center" style={{ width: "45px" }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={
                            visitorEnquiries.length > 0 &&
                            visitorEnquiries.every((v) => selectedVisitorIds.includes(v._id || v.id))
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedVisitorIds(visitorEnquiries.map((v) => v._id || v.id));
                            } else {
                              setSelectedVisitorIds([]);
                            }
                          }}
                        />
                      </th>
                    )}
                    <th className="text-white fw-bold py-16 px-24" style={{ width: "70px" }}>Sl.No</th>
                    <th className="text-white fw-bold py-16 px-24" style={{ width: "160px" }}>Date</th>
                    <th className="text-white fw-bold py-16 px-24" style={{ width: "180px" }}>Name</th>
                    <th className="text-white fw-bold py-16 px-24" style={{ width: "180px" }}>Company</th>
                    <th className="text-white fw-bold py-16 px-24" style={{ width: "180px" }}>Contact Info</th>
                    <th className="text-white fw-bold py-16 px-24" style={{ width: "180px" }}>Known Source</th>
                    <th className="text-white fw-bold py-16 px-24 text-center" style={{ width: "130px" }}>Source</th>
                    <th className="text-white fw-bold py-16 px-24 text-center" style={{ width: "120px" }}>Action</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {mainTab === "stalls" ? (
                  stallBookings.length > 0 ? (
                    stallBookings
                      .slice(
                        currentPage * rowsPerPage,
                        (currentPage + 1) * rowsPerPage,
                      )
                      .map((item, idx) => (
                        <tr key={item._id || item.id}>
                          <td className="px-24 fw-bold text-dark">
                            {item.name}
                          </td>
                          <td className="px-24 text-secondary-light text-sm">
                            {item.companyName || "N/A"}
                          </td>
                          <td className="px-24 text-center">
                            <span
                              className="badge px-12 py-6 radius-4 fw-bold uppercase"
                              style={{
                                letterSpacing: "0.5px",
                                backgroundColor:
                                  item.source &&
                                    item.source.toLowerCase().trim() === "website"
                                     ? "#ff7828"
                                     : "#e6f2ff",
                                color:
                                  item.source &&
                                    item.source.toLowerCase().trim() === "website"
                                     ? "#ffffff"
                                     : "#0066cc",
                                minWidth: "80px",
                                display: "inline-block",
                              }}
                            >
                              {item.source && item.source.trim() !== ""
                                ? item.source
                                : "Admin"}
                            </span>
                          </td>
                          <td className="px-24">
                            <span className="badge bg-info-focus text-info-main radius-4 px-10 py-4 text-xxs uppercase">
                              {typeof item.category === "object"
                                ? item.category?.name ||
                                item.category?.categoryName
                                : item.category || "General"}
                            </span>
                          </td>
                          <td className="px-24 text-secondary-light text-sm">
                            <div className="d-flex align-items-center justify-content-between gap-2 w-100">
                              {item.products && item.products.length > 0 ? (
                                <>
                                  <span className="badge bg-neutral-100 text-secondary-light border radius-4 px-10 py-6 text-xs fw-semibold">
                                    {item.products[0].productName || item.products[0].name}
                                  </span>
                                  {item.products.length > 1 && (
                                    <span
                                      className="badge bg-danger-focus text-danger-main border border-danger-main radius-4 px-8 py-6 cursor-pointer fw-bold hover-scale-xs ms-auto"
                                      onClick={() => {
                                        setSelectedStallProducts(item.products);
                                        setShowProductModal(true);
                                      }}
                                      style={{ fontSize: "11px" }}
                                      title="View all products"
                                    >
                                      +{item.products.length - 1}
                                    </span>
                                  )}
                                </>
                              ) : (
                                "N/A"
                              )}
                            </div>
                          </td>
                          <td className="px-24">
                            <span className="badge bg-danger-focus text-danger-main radius-4 fw-bold px-12 py-6">
                              {item.stallNo || "N/A"}
                            </span>
                          </td>
                          <td className="px-24 text-secondary-light text-sm">
                            {item.stallSize || "N/A"}
                          </td>
                          <td className="px-24 text-nowrap text-dark fw-semibold" style={{ fontSize: "13px" }}>
                            ₹{Number(item.stallPrice || 0).toLocaleString("en-IN")}
                          </td>
                          <td className="px-24 text-nowrap text-dark" style={{ fontSize: "13px" }}>
                            ₹{Number(item.totalAmount || 0).toLocaleString("en-IN")}
                          </td>
                          <td className="px-24 text-nowrap text-danger" style={{ fontSize: "13px" }}>
                            ₹{Number(item.discount || 0).toLocaleString("en-IN")}
                          </td>
                          <td className="px-24 text-nowrap text-success-600 fw-bold" style={{ fontSize: "13px" }}>
                            ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                          </td>
                          <td className="px-24 text-center">
                            <span className={`badge ${
                              item.approvalStatus === 'Approved'
                                ? 'bg-success-focus text-success-main'
                                : item.approvalStatus === 'Rejected'
                                ? 'bg-danger-focus text-danger-main'
                                : 'bg-warning-focus text-warning-main'
                            } radius-pill px-10 py-4 text-xxs fw-bold uppercase`}>
                              {item.approvalStatus || 'Pending'}
                            </span>
                          </td>
                          <td className="px-24 text-secondary-light text-sm">
                            {item.createdBy || 'Admin'}
                          </td>
                          <td className="px-24 text-secondary-light text-sm">
                            {item.blockedDate ? formatDate(item.blockedDate) : '-'}
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td
                        colSpan="14"
                        className="text-center py-40 text-secondary-light"
                      >
                        No stall bookings found for this past expo.
                      </td>
                    </tr>
                  )
                ) : visitorEnquiries.length > 0 ? (
                  visitorEnquiries.map((item, idx) => (
                    <tr key={item._id || item.id || idx}>
                      {isPropertyExpo && (
                        <td className="px-16 text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedVisitorIds.includes(item._id || item.id)}
                            onChange={(e) => {
                              const id = item._id || item.id;
                              if (e.target.checked) {
                                setSelectedVisitorIds((prev) => [...prev, id]);
                              } else {
                                setSelectedVisitorIds((prev) => prev.filter((x) => x !== id));
                              }
                            }}
                          />
                        </td>
                      )}
                      <td className="px-24 text-secondary-light text-sm">
                        {currentPage * rowsPerPage + idx + 1}
                      </td>
                      <td className="px-24 fw-semibold text-secondary-light" style={{ fontSize: "13px" }}>
                        {formatDateTime(item.createdAt || item.date)}
                      </td>
                      <td className="px-24 fw-bold text-dark">{item.name}</td>
                      <td className="px-24 text-secondary-light text-sm">
                        {item.companyName || "N/A"}
                      </td>
                      <td className="px-24 text-secondary-light text-sm">
                        <div className="d-flex flex-column">
                          <span className="fw-semibold">{item.mobileNo || item.phone || "N/A"}</span>
                          <span className="text-secondary-light text-xs">{item.email || ""}</span>
                        </div>
                      </td>
                      <td className="px-24 text-secondary-light" style={{ fontSize: "13px" }}>
                        {item.knowAboutExhibition || "-"}
                      </td>
                      <td className="px-24 text-center">
                        <span
                          className="badge px-12 py-6 radius-4 fw-bold uppercase"
                          style={{
                            letterSpacing: "0.5px",
                            backgroundColor:
                              item.source &&
                                item.source.toLowerCase().trim() === "website"
                                ? "#ff7828"
                                : item.source &&
                                  item.source.toLowerCase().trim() === "mobile-admin"
                                  ? "#e6f2ff"
                                  : "#f0fdf4",
                            color:
                              item.source &&
                                item.source.toLowerCase().trim() === "website"
                                ? "#ffffff"
                                : item.source &&
                                  item.source.toLowerCase().trim() === "mobile-admin"
                                  ? "#0066cc"
                                  : "#16a34a",
                            minWidth: "80px",
                            display: "inline-block",
                          }}
                        >
                          {item.source && item.source.trim() !== ""
                            ? item.source === "mobile-admin"
                              ? "Admin-App"
                              : item.source === "user-app"
                                ? "User-app"
                                : item.source.charAt(0).toUpperCase() + item.source.slice(1)
                            : "Admin"}
                        </span>
                      </td>
                      <td className="px-24 text-center">
                        <div className="d-flex align-items-center justify-content-center gap-8">
                          <button
                            type="button"
                            className="glass-action-btn glass-btn-view"
                            title="View Details"
                            style={{ width: "30px", height: "30px" }}
                            onClick={() => {
                              setSelectedLead({
                                ...item,
                                FromType: "Visitor"
                              });
                              setShowHistoryModal(true);
                            }}
                          >
                            <Icon
                              icon="solar:eye-bold-duotone"
                              style={{ fontSize: "16px" }}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-40 text-secondary-light"
                    >
                      No visitor bookings found for this past expo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="p-20 bg-neutral-10 border-top">
            <TablePagination
              currentPage={currentPage}
              totalPages={
                Math.ceil(
                  (mainTab === "stalls"
                    ? stallBookings.length
                    : visitorTotal) / rowsPerPage,
                ) || 1
              }
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
              totalRecords={
                mainTab === "stalls"
                  ? stallBookings.length
                  : visitorTotal
              }
            />
          </div>
        </div>
      </div>

      {/* Product List Modal */}
      <Modal
        show={showProductModal}
        onHide={() => setShowProductModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header 
          closeButton 
          className="border-bottom py-16 px-24"
          style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}
        >
          <Modal.Title className="text-white text-md fw-bold">
            Stall Product List
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-24">
          <div className="d-flex flex-wrap gap-2">
            {selectedStallProducts.map((p, idx) => (
              <span
                key={p._id || idx}
                className="badge bg-neutral-100 text-secondary-light border radius-8 px-12 py-8 text-xs fw-semibold shadow-xs"
              >
                {p.productName || p.name}
              </span>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top py-12 px-24">
          <Button
            variant="secondary"
            onClick={() => setShowProductModal(false)}
            className="radius-8 px-20 py-8 fw-bold text-xs"
            style={{ backgroundColor: "#ed1c24", border: "none" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Visitor Lead Details Modal */}
      {showHistoryModal && selectedLead && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg overflow-hidden">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000, #bb0007, #ed1c24)",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="w-40-px h-40-px rounded-circle bg-white-10 d-flex align-items-center justify-content-center text-white border-white-10">
                    <Icon icon="solar:user-bold" />
                  </div>
                  <div>
                    <h6 className="modal-title text-white mb-0">
                      Visitor Details - {selectedLead.name}
                    </h6>
                    <span className="text-white-50 text-xs">
                      {selectedLead.companyName || "No Company Specified"}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowHistoryModal(false)}
                ></button>
              </div>
              <div
                className="modal-body p-24 scroll-sm"
                style={{ maxHeight: "75vh", overflowY: "auto" }}
              >
                <div className="row gy-4">
                  <div className="col-12">
                    <div className="p-20 radius-12 bg-neutral-50 border">
                      <h6 className="text-xs fw-bold text-secondary-light uppercase mb-20 border-bottom pb-8">
                        Visitor Registration Information
                      </h6>
                      <div className="row gy-4">
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Visitor Name</span>
                          <span className="fw-semibold text-dark text-sm">{selectedLead.name || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Company Name</span>
                          <span className="fw-semibold text-dark text-sm">{selectedLead.companyName || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Mobile Number</span>
                          <span className="fw-semibold text-dark text-sm">{selectedLead.mobileNo || selectedLead.phone || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Email Address</span>
                          <span className="fw-semibold text-dark text-sm">{selectedLead.email || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">City</span>
                          <span className="fw-semibold text-dark text-sm">{selectedLead.city || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Business Category</span>
                          <span className="fw-semibold text-dark text-sm">{selectedLead.businessCategory || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Product of Interest</span>
                          <span className="fw-semibold text-dark text-sm">{selectedLead.productOfInterest || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">How do you know about this Exhibition ?</span>
                          <span className="fw-semibold text-dark text-sm">{selectedLead.knowAboutExhibition || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Date</span>
                          <span className="fw-semibold text-dark text-sm">{formatDateTime(selectedLead.createdAt || selectedLead.date)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top py-12 px-24">
                <Button
                  variant="secondary"
                  onClick={() => setShowHistoryModal(false)}
                  className="radius-8 px-20 py-8 fw-bold text-xs"
                  style={{ backgroundColor: "#ed1c24", border: "none" }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PastExpoViewLayer;
